import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import { AdminSchoolsPanel } from '../AdminSchools';
import { AdminSectorsPanel } from '../AdminSectors';
import { AdminFieldsPanel } from '../AdminFields';
import { adminService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import './styles.css';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  // Status modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [nextStatus, setNextStatus] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [roleUpdatingUserId, setRoleUpdatingUserId] = useState(null);
  const [roleConfirmModalOpen, setRoleConfirmModalOpen] = useState(false);
  
  // Add report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ tier: 'Beta', report_type: '', report_uuid: '' });
  const [savingReport, setSavingReport] = useState(false);
  
  // Change tier modal state
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [tierConfirmModalOpen, setTierConfirmModalOpen] = useState(false);
  const [nextTier, setNextTier] = useState('B');
  const [tierUpdating, setTierUpdating] = useState(false);
  
  // View details modal state
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [viewDetailsUser, setViewDetailsUser] = useState(null);
  const [viewDetailsReports, setViewDetailsReports] = useState({ Beta: [], Alpha: [], 'Alpha+': [] });
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [viewDetailsError, setViewDetailsError] = useState('');
  const ADMIN_TABS = [
    { key: 'users', label: 'Users' },
    { key: 'entities', label: 'Entities' },
    // { key: 'templates', label: 'Templates' },
    { key: 'sectors', label: 'Sectors' },
    { key: 'fields', label: 'Fields' }
  ];
  const [activeTab, setActiveTab] = useState(ADMIN_TABS[0].key);
  const tabRefs = useRef({});
  const underlineRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await adminService.listUsers();
        if (mounted) setUsers(data);
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load users');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => (u.full_name || u.username || '').toLowerCase().includes(q))
      .filter((u) => {
        if (statusFilter === 'active') return !!u.is_active;
        if (statusFilter === 'inactive') return !u.is_active;
        return true;
      });
  }, [users, query, statusFilter]);

  const totalUsers = users.length;
  const activeUsers = useMemo(() => users.filter((u) => u.is_active).length, [users]);
  const inactiveUsers = totalUsers - activeUsers;
  const skeletonCards = Array.from({ length: 8 });
  const statusChips = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active only' },
    { key: 'inactive', label: 'Inactive only' }
  ];

  const syncTabToUrl = (tabKey) => {
    const params = new URLSearchParams(location.search);
    params.set('section', tabKey);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('section');
    const found = ADMIN_TABS.find((tab) => tab.key === requested)?.key || ADMIN_TABS[0].key;
    setActiveTab(found);
  }, [location.search]);

  useEffect(() => {
    const underlineEl = underlineRef.current;
    const tabsEl = underlineEl?.parentElement;
    if (!underlineEl || !tabsEl) return;

    const updateUnderline = () => {
      const tabEl = tabRefs.current[activeTab];
      if (!tabEl) return;
      underlineEl.style.width = `${tabEl.offsetWidth}px`;
      underlineEl.style.transform = `translateX(${tabEl.offsetLeft}px)`;
    };

    updateUnderline();
    const observer = new ResizeObserver(updateUnderline);
    observer.observe(tabsEl);
    window.addEventListener('resize', updateUnderline);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateUnderline);
    };
  }, [activeTab]);

  const renderUsersSection = () => (
    <>

      <div className="admin-search-card">
        <input
          className="admin-dashboard-search"
          type="text"
          placeholder="Search users by full name or username..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="admin-filter-row">
          <div className="admin-filter-group">
            <span className="admin-filter-label">Status</span>
            <div className="admin-filter-chips">
              {statusChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className={`admin-filter-chip ${statusFilter === chip.key ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(chip.key)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
          <div className="admin-filter-stats">
            <div className="admin-stat-card">
              <span className="admin-stat-label">Total users</span>
              <strong>{totalUsers}</strong>
            </div>
            <div className="admin-stat-card admin-stat-card--active">
              <span className="admin-stat-label">Active</span>
              <strong>{activeUsers}</strong>
            </div>
            <div className="admin-stat-card admin-stat-card--inactive">
              <span className="admin-stat-label">Inactive</span>
              <strong>{inactiveUsers}</strong>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-users-table">
        <table className="admin-users-grid">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">User ID</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col" className="actions-col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? skeletonCards.map((_, idx) => (
                <tr key={`row-skeleton-${idx}`} className="skeleton-row">
                  <td>
                    <span className="skeleton-line" />
                  </td>
                  <td>
                    <span className="skeleton-line" />
                  </td>
                  <td>
                    <span className="skeleton-line" />
                  </td>
                  <td>
                    <span className="skeleton-line" />
                  </td>
                  <td>
                    <span className="skeleton-line" />
                  </td>
                  <td>
                    <span className="skeleton-pill" />
                  </td>
                </tr>
              ))
              : filteredUsers.map((u, idx) => (
                <tr key={u.user_id || u.email || u.username || idx}>
                  <td className="cell-strong">
                    {u.full_name || u.username || 'Unnamed User'}
                  </td>
                  <td className="cell-mono">{u.user_id || 'Unknown'}</td>
                  <td className="cell-muted">{u.email || 'No Email'}</td>
                  <td>{u.role || 'Member'}</td>
                  <td>
                    <span
                      className={`admin-status-dot ${u.is_active ? 'is-active' : 'is-inactive'}`}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-col">
                    <div className="admin-table-actions">
                      <button
                        onClick={() => handleViewDetails(u)}
                        className="admin-more-btn"
                      >
                        View Details
                      </button>
                      <button
                        className={`admin-toggle-status-btn ${u.is_active ? 'deactivate' : 'activate'}`}
                        onClick={() => {
                          setSelectedUser(u);
                          setNextStatus(!u.is_active);
                          setStatusModalOpen(true);
                        }}
                        disabled={statusUpdating}
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="admin-add-report-btn"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowReportModal(true);
                        }}
                      >
                        Add Report
                      </button>
                      <button
                        className="admin-change-tier-btn"
                        onClick={() => {
                          setSelectedUser(u);
                          setNextTier(u.product_tier || 'B');
                          setTierModalOpen(true);
                        }}
                        disabled={tierUpdating}
                      >
                        Change Tier
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!loading && !error && filteredUsers.length === 0 && (
          <div className="admin-empty-state admin-empty-state--flush">
            <p>No users matched your search.</p>
            <span>Try adjusting the text search or status filter above.</span>
          </div>
        )}
      </div>
    </>
  );

  const renderEntitiesSection = () => (
    <div className="admin-schools-panel-wrapper">
      <AdminSchoolsPanel embedded />
    </div>
  );

  const renderTemplatesSection = () => (
    <div className="admin-panel-placeholder">
      <h3>Templates</h3>
      <span className="admin-pill-muted">Nothing to show yet</span>
    </div>
  );

  const renderSectorsSection = () => (
    <div className="admin-schools-panel-wrapper">
      <AdminSectorsPanel embedded />
    </div>
  );

  const renderFieldsSection = () => (
    <div className="admin-schools-panel-wrapper">
      <AdminFieldsPanel embedded />
    </div>
  );

  const renderActiveSection = () => {
    switch (activeTab) {
      case 'users':
        return renderUsersSection();
      case 'entities':
        return renderEntitiesSection();
      case 'templates':
        return renderTemplatesSection();
      case 'sectors':
        return renderSectorsSection();
      case 'fields':
        return renderFieldsSection();
      default:
        return null;
    }
  };

  const activeTabMeta = ADMIN_TABS.find((tab) => tab.key === activeTab);

  const handleViewDetails = async (user) => {
    setViewDetailsModalOpen(true);
    setViewDetailsUser(null);
    setViewDetailsReports({ Beta: [], Alpha: [], 'Alpha+': [] });
    setViewDetailsError('');
    setViewDetailsLoading(true);
    setRoleConfirmModalOpen(false);
    setRoleUpdatingUserId(null);
    
    try {
      const data = await adminService.getUserDetails(user.user_id);
      setViewDetailsUser(data.user);
      const incomingReports = data.reports || {};
      setViewDetailsReports({
        Beta: incomingReports.Beta || [],
        Alpha: incomingReports.Alpha || [],
        'Alpha+': incomingReports['Alpha+'] || []
      });
    } catch (e) {
      setViewDetailsError(e.message || 'Failed to load user details');
    } finally {
      setViewDetailsLoading(false);
    }
  };

  // Escape key handlers for modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (viewDetailsModalOpen && !viewDetailsLoading) {
          setViewDetailsModalOpen(false);
        } else if (showReportModal && !savingReport) {
          setShowReportModal(false);
        } else if (tierModalOpen && !tierUpdating) {
          setTierModalOpen(false);
        }
      }
    };

    if (viewDetailsModalOpen || showReportModal || tierModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [viewDetailsModalOpen, showReportModal, tierModalOpen, viewDetailsLoading, savingReport, tierUpdating]);

  return (
    <SidebarLayout>
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <div className="admin-title-group">
            <h1>Admin Dashboard</h1>
          </div>
        </div>

        <div className="admin-subnav">
          <div className="admin-subnav-tabs">
            {ADMIN_TABS.map((tab) => (
              <button
                key={tab.key}
                ref={(node) => {
                  if (node) tabRefs.current[tab.key] = node;
                }}
                type="button"
                className={`admin-subnav-btn ${activeTab === tab.key ? 'is-active' : ''}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  syncTabToUrl(tab.key);
                }}
              >
                <span className="admin-subnav-label">{tab.label}</span>
              </button>
            ))}
            <span className="admin-subnav-underline" ref={underlineRef} />
          </div>
        </div>

        <div className="admin-tab-panels">
          <div className={`admin-tab-panel ${activeTab === 'users' ? 'is-active' : ''}`}>
            {activeTab === 'users' && renderUsersSection()}
          </div>
          <div className={`admin-tab-panel ${activeTab === 'entities' ? 'is-active' : ''}`}>
            {activeTab === 'entities' && renderEntitiesSection()}
          </div>
          <div className={`admin-tab-panel ${activeTab === 'templates' ? 'is-active' : ''}`}>
            {activeTab === 'templates' && renderTemplatesSection()}
          </div>
          <div className={`admin-tab-panel ${activeTab === 'sectors' ? 'is-active' : ''}`}>
            {activeTab === 'sectors' && renderSectorsSection()}
          </div>
          <div className={`admin-tab-panel ${activeTab === 'fields' ? 'is-active' : ''}`}>
            {activeTab === 'fields' && renderFieldsSection()}
          </div>
        </div>

        {/* Status Confirmation Modal */}
        <ConfirmationModal
          isOpen={statusModalOpen}
          onClose={() => (!statusUpdating ? setStatusModalOpen(false) : null)}
          onConfirm={async () => {
            if (!selectedUser) return;
            try {
              setStatusUpdating(true);
              await adminService.updateUserStatus(selectedUser.user_id, nextStatus);
              setUsers((prev) =>
                prev.map((u) =>
                  u.user_id === selectedUser.user_id ? { ...u, is_active: nextStatus } : u
                )
              );
              showSuccess(nextStatus ? 'User activated successfully' : 'User deactivated successfully');
              setStatusModalOpen(false);
              setSelectedUser(null);
            } catch (e) {
              showError(e.message || 'Failed to update user status');
            } finally {
              setStatusUpdating(false);
            }
          }}
          loading={statusUpdating}
          title={nextStatus ? 'Activate user?' : 'Deactivate user?'}
          message={
            nextStatus
              ? 'This user will regain access to their reports immediately.'
              : 'The user will lose access to all portals until re-activated. Are you sure you want to continue?'
          }
          confirmText={nextStatus ? 'Activate' : 'Deactivate'}
          icon={nextStatus ? '✅' : '⚠️'}
        />

        {/* Add Report Modal */}
        {showReportModal && (
          <div className="admin-modal-overlay" onClick={() => !savingReport && setShowReportModal(false)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal-header">
                <div>Add Report{selectedUser ? ` - ${selectedUser.full_name || selectedUser.username || 'User'}` : ''}</div>
                <button className="admin-btn admin-btn-secondary" onClick={() => !savingReport && setShowReportModal(false)}>✕</button>
              </div>
              <div className="admin-modal-body">
                <div className="admin-modal-row">
                  <label>Tier *</label>
                  <select
                    className="admin-select"
                    value={reportForm.tier}
                    onChange={(e) => setReportForm((f) => ({ ...f, tier: e.target.value }))}
                  >
                    <option value="Beta">Beta</option>
                    <option value="Alpha">Alpha</option>
                    <option value="Alpha+">Alpha+</option>
                  </select>
                </div>
                <div className="admin-modal-row">
                  <label>Report Type *</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={reportForm.report_type}
                    onChange={(e) => setReportForm((f) => ({ ...f, report_type: e.target.value }))}
                    placeholder="e.g. Sales"
                  />
                </div>
                <div className="admin-modal-row">
                  <label>Report UUID *</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={reportForm.report_uuid}
                    onChange={(e) => setReportForm((f) => ({ ...f, report_uuid: e.target.value }))}
                    placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                  />
                </div>
              </div>
              <div className="admin-modal-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  disabled={savingReport}
                  onClick={() => !savingReport && setShowReportModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn admin-btn-primary"
                  disabled={savingReport || !reportForm.tier || !reportForm.report_type || !reportForm.report_uuid || !selectedUser}
                  onClick={async () => {
                    if (!selectedUser) return;
                    try {
                      setSavingReport(true);
                      await adminService.addUserReport(selectedUser.user_id, reportForm);
                      showSuccess('Report added successfully');
                      setShowReportModal(false);
                      setReportForm({ tier: 'Beta', report_type: '', report_uuid: '' });
                      setSelectedUser(null);
                    } catch (e) {
                      showError(e.message || 'Failed to add report');
                    } finally {
                      setSavingReport(false);
                    }
                  }}
                >
                  {savingReport ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Tier Modal */}
        {tierModalOpen && (
          <div className="admin-modal-overlay" onClick={() => !tierUpdating && setTierModalOpen(false)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="admin-modal-header">
                <div>Change Product Tier</div>
                <button className="admin-btn admin-btn-secondary" onClick={() => !tierUpdating && setTierModalOpen(false)}>✕</button>
              </div>
              <div className="admin-modal-body">
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px', color: '#92400e' }}>⚠️ Important: Subscription Change</div>
                  <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                    Changing the product tier will immediately affect the user's access to dashboard reports:
                    <ul style={{ marginTop: '8px', marginLeft: '20px', padding: 0 }}>
                      <li><strong>B:</strong> Access to Beta reports only</li>
                      <li><strong>A:</strong> Access to Beta and Alpha reports</li>
                      <li><strong>A+:</strong> Access to all reports (Beta, Alpha, Alpha+)</li>
                    </ul>
                  </div>
                </div>
                <div className="admin-modal-row">
                  <label>Current Tier</label>
                  <div style={{ 
                    padding: '6px 12px', 
                    borderRadius: '4px', 
                    backgroundColor: (selectedUser?.product_tier || 'B') === 'A+' ? '#10b981' : (selectedUser?.product_tier || 'B') === 'A' ? '#3b82f6' : '#6b7280',
                    color: 'white',
                    fontWeight: '500',
                    display: 'inline-block'
                  }}>
                    {selectedUser?.product_tier || 'B'}
                  </div>
                </div>
                <div className="admin-modal-row">
                  <label>New Tier</label>
                  <select
                    className="admin-select"
                    value={nextTier}
                    onChange={(e) => setNextTier(e.target.value)}
                    disabled={tierUpdating}
                  >
                    <option value="B">B - Beta Only</option>
                    <option value="A">A - Beta & Alpha</option>
                    <option value="A+">A+ - All Tiers</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  disabled={tierUpdating}
                  onClick={() => !tierUpdating && setTierModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn admin-btn-primary"
                  disabled={tierUpdating || nextTier === (selectedUser?.product_tier || 'B')}
                  onClick={() => {
                    if (!selectedUser || nextTier === (selectedUser.product_tier || 'B')) return;
                    setTierModalOpen(false);
                    setTierConfirmModalOpen(true);
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tier Change Confirmation Modal */}
        <ConfirmationModal
          isOpen={tierConfirmModalOpen}
          onClose={() => (!tierUpdating ? setTierConfirmModalOpen(false) : null)}
          onConfirm={async () => {
            if (!selectedUser) return;
            try {
              setTierUpdating(true);
              await adminService.updateUserTier(selectedUser.user_id, nextTier);
              setUsers((prev) =>
                prev.map((u) =>
                  u.user_id === selectedUser.user_id ? { ...u, product_tier: nextTier } : u
                )
              );
              showSuccess(`User tier updated to ${nextTier} successfully`);
              setTierConfirmModalOpen(false);
              setTierModalOpen(false);
              setSelectedUser(null);
            } catch (e) {
              showError(e.message || 'Failed to update user tier');
            } finally {
              setTierUpdating(false);
            }
          }}
          loading={tierUpdating}
          title="Confirm Tier Change"
          message={
            `Are you sure you want to change ${selectedUser?.full_name || selectedUser?.username || 'this user'}'s product tier from ${selectedUser?.product_tier || 'B'} to ${nextTier}?\n\n` +
            `This will immediately affect their access:\n` +
            (nextTier === 'B' ? '• Access to Beta reports only' : '') +
            (nextTier === 'A' ? '• Access to Beta and Alpha reports' : '') +
            (nextTier === 'A+' ? '• Access to all reports (Beta, Alpha, Alpha+)' : '') +
            `\n\nThis action cannot be easily undone.`
          }
          confirmText="Confirm Change"
          icon="⚠️"
        />

        {/* Make Admin Confirmation Modal */}
        <ConfirmationModal
          isOpen={roleConfirmModalOpen}
          onClose={() => (!roleUpdatingUserId ? setRoleConfirmModalOpen(false) : null)}
          onConfirm={async () => {
            if (!viewDetailsUser || viewDetailsUser.role === 'admin') {
              setRoleConfirmModalOpen(false);
              return;
            }
            try {
              setRoleUpdatingUserId(viewDetailsUser.user_id);
              const updated = await adminService.updateUserRole(viewDetailsUser.user_id, 'admin');
              setUsers((prev) =>
                prev.map((u) =>
                  u.user_id === viewDetailsUser.user_id ? { ...u, role: updated?.role || 'admin' } : u
                )
              );
              setViewDetailsUser((prev) => (prev ? { ...prev, role: updated?.role || 'admin' } : prev));
              showSuccess('User promoted to admin successfully');
            } catch (e) {
              showError(e.message || 'Failed to update user role');
            } finally {
              setRoleUpdatingUserId(null);
              setRoleConfirmModalOpen(false);
            }
          }}
          loading={!!roleUpdatingUserId}
          title="Promote to Admin"
          message={`Are you sure you want to make ${viewDetailsUser?.full_name || viewDetailsUser?.username || 'this user'} an admin? They will gain full administrative access.`}
          confirmText="Make Admin"
          icon="⚠️"
        />

        {/* View Details Modal */}
        {viewDetailsModalOpen && (
          <div className="admin-modal-overlay admin-view-details-overlay" onClick={() => setViewDetailsModalOpen(false)}>
            <div className="admin-view-details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-view-details-header">
                <div className="admin-view-details-header-content">
                  <h2 className="admin-view-details-title">User Details</h2>
                  {viewDetailsUser && (
                    <div className="admin-view-details-status-badge">
                      <span className={`admin-view-details-status-dot ${viewDetailsUser.is_active ? 'is-active' : 'is-inactive'}`} />
                      <span>{viewDetailsUser.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  )}
                </div>
                <button 
                  className="admin-view-details-close-btn" 
                  onClick={() => setViewDetailsModalOpen(false)}
                  aria-label="Close"
                  tabIndex={0}
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 5L5 15M5 5l10 10" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <div className="admin-view-details-body">
                {viewDetailsLoading && (
                  <div className="admin-view-details-loading">
                    <div className="admin-skeleton-grid">
                      <div className="skeleton-block" />
                      <div className="skeleton-block" />
                      <div className="skeleton-block" />
                      <div className="skeleton-block" />
                      <div className="skeleton-block" />
                      <div className="skeleton-block" />
                    </div>
                  </div>
                )}
                {viewDetailsError && (
                  <div className="admin-view-details-error">
                    {viewDetailsError}
                  </div>
                )}
                {!viewDetailsLoading && viewDetailsUser && (
                  <>
                    <div className="admin-view-details-info-section">
                      <h3 className="admin-view-details-section-title">Personal Information</h3>
                      <div className="admin-user-grid">
                        <div className="admin-info-item">
                          <div className="admin-info-label">Username</div>
                          <div className="admin-info-value">{viewDetailsUser.username || 'NA'}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Email</div>
                          <div className="admin-info-value">{viewDetailsUser.email || 'NA'}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Location</div>
                          <div className="admin-info-value">{viewDetailsUser.location || 'NA'}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Mobile</div>
                          <div className="admin-info-value">{viewDetailsUser.mobile_number || 'NA'}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Full Name</div>
                          <div className="admin-info-value">{viewDetailsUser.full_name || 'NA'}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">User ID</div>
                          <div className="admin-info-value">{viewDetailsUser.user_id}</div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Status</div>
                          <div className={`admin-info-value ${viewDetailsUser.is_active ? 'status-active' : 'status-inactive'}`}>
                            {viewDetailsUser.is_active ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                        <div className="admin-info-item">
                          <div className="admin-info-label">Sector</div>
                          <div className="admin-info-value">{viewDetailsUser.sector_name || 'NA'}</div>
                        </div>
                            <div className="admin-info-card admin-info-card--muted">
                              <div className="admin-info-label">Product Tier</div>
                              <div className="admin-info-card-value">
                                <span
                                  className="admin-tier-pill"
                                  data-tier={viewDetailsUser.product_tier || 'B'}
                                >
                                  {viewDetailsUser.product_tier || 'B'}
                                </span>
                              </div>
                            </div>
                            <div className="admin-info-card admin-info-card--muted">
                              <div className="admin-info-label">Role</div>
                              <div className="admin-info-card-value admin-role-row">
                                <span className="admin-role-label">
                                  {viewDetailsUser.role || 'Member'}
                                </span>
                                <button
                                  className="admin-btn admin-btn-promote"
                                  style={{ padding: '8px 14px', minWidth: '140px' }}
                                  disabled={viewDetailsUser.role === 'admin' || roleUpdatingUserId === viewDetailsUser.user_id}
                                  onClick={() => {
                                    setViewDetailsModalOpen(false);
                                    setRoleConfirmModalOpen(true);
                                  }}
                                >
                                  {viewDetailsUser.role === 'admin'
                                    ? 'Already Admin'
                                    : roleUpdatingUserId === viewDetailsUser.user_id
                                    ? 'Promoting...'
                                    : 'Make Admin'}
                                </button>
                              </div>
                        </div>
                        {viewDetailsUser.entity_id && (
                          <div className="admin-info-item">
                            <div className="admin-info-label">Entity ID</div>
                            <div className="admin-info-value">{viewDetailsUser.entity_id}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="admin-view-details-reports-section">
                      <h3 className="admin-view-details-section-title">Reports</h3>
                      <div className="admin-reports-grid">
                        <div className="admin-reports-section">
                        <div className="admin-section-header tier-beta">
                          <div className="admin-section-title">
                            <span>Beta</span>
                            <span className="admin-section-count">{viewDetailsReports.Beta.length}</span>
                          </div>
                        </div>
                        {viewDetailsReports.Beta.map((r, idx) => (
                          <div key={`b-${idx}`} className="admin-report-item">
                            <span className="admin-report-dot"></span>
                            <span>{r.report_type}</span>
                          </div>
                        ))}
                        {viewDetailsReports.Beta.length === 0 && (
                          <div className="admin-view-details-empty-reports">No reports</div>
                        )}
                      </div>

                      <div className="admin-reports-section">
                        <div className="admin-section-header tier-alpha">
                          <div className="admin-section-title">
                            <span>Alpha</span>
                            <span className="admin-section-count">{viewDetailsReports.Alpha.length}</span>
                          </div>
                        </div>
                        {viewDetailsReports.Alpha.map((r, idx) => (
                          <div key={`a-${idx}`} className="admin-report-item">
                            <span className="admin-report-dot"></span>
                            <span>{r.report_type}</span>
                          </div>
                        ))}
                        {viewDetailsReports.Alpha.length === 0 && (
                          <div className="admin-view-details-empty-reports">No reports</div>
                        )}
                      </div>

                      <div className="admin-reports-section">
                        <div className="admin-section-header tier-alphap">
                          <div className="admin-section-title">
                            <span>Alpha+</span>
                            <span className="admin-section-count">{viewDetailsReports['Alpha+'].length}</span>
                          </div>
                        </div>
                        {viewDetailsReports['Alpha+'].map((r, idx) => (
                          <div key={`ap-${idx}`} className="admin-report-item">
                            <span className="admin-report-dot"></span>
                            <span>{r.report_type}</span>
                          </div>
                        ))}
                        {viewDetailsReports['Alpha+'].length === 0 && (
                          <div className="admin-view-details-empty-reports">No reports</div>
                        )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default AdminDashboard;


